// Row-value formatting for the plan view (SessionPlanDisplay).
//
// Extracted as its own pure module — alongside planTheme.js and planDnd.js — so
// it can be unit-tested without importing the component, which pulls in llmApi
// and its ESM-only Gemini dependency.

import { describeSetScheme, formatSetSchemeValues } from "../../utils/planVisuals";

/**
 * Build the two strings a plan row shows for its value: `denseString` for the
 * right-hand column and `verboseString` for its tooltip.
 *
 * Handles the legacy `sets`/`rounds`/`duration`/`reps` fields that older saved
 * plans still carry, alongside the canonical `value`/`unit` pair.
 */
export const formatIntervalData = (interval) => {
  const sets = interval.sets || interval.rounds;

  const rawValue = interval.value ?? interval.duration ?? interval.reps;
  const rawUnit =
    interval.unit || (interval.duration ? "s" : interval.reps ? "reps" : "");

  let denseString = "";
  let verboseString = "";

  if (sets) {
    denseString += `${sets}x `;
    verboseString += `${sets} ${interval.type === "Group" ? "rounds" : "sets"} of `;
  }

  // Per-set scheme (issue #42): the prescription is the whole ramp — 8/7/6/5
  // reps — not the static field, which by schema rule mirrors set 1 only. Show
  // that single figure and a four-set ramp reads as "8 reps every set".
  const perSetValues = formatSetSchemeValues(interval);
  if (perSetValues) {
    return {
      denseString: (denseString + perSetValues).trim(),
      verboseString: describeSetScheme(interval),
    };
  }

  const distanceM = Number(interval.estimatedFromDistanceM);
  if (Number.isFinite(distanceM) && distanceM > 0) {
    // Distance target (issue #63): stored as estimated seconds + provenance,
    // but the metres are what was prescribed.
    denseString += `${Math.round(distanceM)}m`;
    verboseString += `${Math.round(distanceM)} metres`;
  } else if (rawValue !== undefined && rawValue !== null && rawValue !== "") {
    if (rawUnit === "s" || rawUnit === "m" || interval.duration) {
      // Single M:SS rule for every duration in the plan view (e.g. 0:30, 1:30,
      // 2:00). Keeps the dense column visually consistent — no more mixed
      // `30s` / `2m` / `1:30` formats on the same screen.
      const totalSeconds = Math.round(rawUnit === "m" ? rawValue * 60 : rawValue);
      const mins = Math.floor(totalSeconds / 60);
      const secs = totalSeconds % 60;
      denseString += `${mins}:${secs.toString().padStart(2, "0")}`;
      if (mins > 0 && secs === 0) {
        verboseString += `${mins} minutes`;
      } else if (mins === 0) {
        verboseString += `${secs} seconds`;
      } else {
        verboseString += `${mins} minutes and ${secs} seconds`;
      }
    } else {
      // FIX: Dynamically add a space for longer string units (like "rounds", "reps", "kg")
      // One of a plural unit reads singular: "1 round", not "1 rounds".
      const displayUnit =
        Number(rawValue) === 1 && ["rounds", "sets", "reps"].includes(rawUnit)
          ? rawUnit.slice(0, -1)
          : rawUnit;
      const space = displayUnit.length > 1 ? " " : "";

      // Inside a sets/rounds Group the unit is already implied by the "4x"
      // prefix, so a rep count stands alone ("4x 8"). A calorie target keeps
      // its unit either way — "4x 20" would read as reps.
      if (sets && rawUnit === "reps") {
        denseString += `${rawValue}`;
      } else {
        denseString += `${rawValue}${space}${displayUnit}`;
      }
      verboseString += `${rawValue} ${displayUnit}`;
    }
  }

  return {
    denseString: denseString.trim(),
    verboseString: verboseString.trim(),
  };
};
